import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { CloudFunctions } from "@services/firebase";
import * as AuthService from "@services/firebase/AuthService.js";
import { useUserStore } from "@/stores/useUserStore";

export function useAuth() {
  const userStore = useUserStore();
  const authState = ref(AuthService.getAuthState());
  let unsubscribe = null;

  async function init() {
    try {
      if (typeof AuthService.initializeAuthService === "function") {
        await AuthService.initializeAuthService();
      }
    } catch (e) {
      console.warn("Auth initialization warning:", e);
    }
  }

  onMounted(async () => {
    await init();
    try {
      unsubscribe = AuthService.addAuthStateListener(() => {
        authState.value = AuthService.getAuthState();
      });
    } catch (e) {
      console.warn("Failed to subscribe to auth state:", e);
    }
  });

  onBeforeUnmount(() => {
    if (typeof unsubscribe === "function") {
      try {
        unsubscribe();
      } catch {}
    }
  });

  const user = computed(() => authState.value.user);
  const isReady = computed(() => authState.value.isReady);
  const isLoggedIn = computed(() => authState.value.isLoggedIn);
  const isAnonymous = computed(() => authState.value.isAnonymous);
  const providers = computed(() => {
    const u = user.value;
    return Array.isArray(u?.providerData)
      ? u.providerData.map((p) => p.providerId)
      : [];
  });

  async function signInWithGoogle(locale, options) {
    try {
      const anon = await AuthService.getAnonymousTokenId();
      if (anon) {
        try {
          sessionStorage.setItem("anonTokenBeforeLogin", anon);
        } catch {}
      }
    } catch {}
    return await AuthService.startGoogleSignIn(locale, options);
  }
  async function signInWithEmailPassword(email, password) {
    try {
      const anon = await AuthService.getAnonymousTokenId();
      if (anon) {
        try {
          sessionStorage.setItem("anonTokenBeforeLogin", anon);
        } catch {}
      }
    } catch {}
    return await AuthService.signInWithEmailPassword(email, password);
  }
  async function registerWithEmailPassword(email, password, displayName) {
    try {
      const anon = await AuthService.getAnonymousTokenId();
      if (anon) {
        try {
          sessionStorage.setItem("anonTokenBeforeLogin", anon);
        } catch {}
      }
    } catch {}
    return await AuthService.registerWithEmailPassword(
      email,
      password,
      displayName
    );
  }
  async function logInToSheets(locale) {
    try {
      const anon = await AuthService.getAnonymousTokenId();
      if (anon) {
        try {
          sessionStorage.setItem("anonTokenBeforeLogin", anon);
        } catch {}
      }
    } catch {}
    return await AuthService.logInToSheets(locale);
  }
  async function logInAnonymously() {
    return await AuthService.logInAnonymously();
  }
  async function logOut() {
    return await AuthService.logOut();
  }

  async function postSignInSetup() {
    await userStore.postSignIn();
  }

  return {
    // reactive state
    user,
    isReady,
    isLoggedIn,
    isAnonymous,
    providers,
    // helpers
    waitForAuthReady: AuthService.waitForAuthReady,
    wasUserJustCreated: AuthService.wasUserJustCreated,
    reloadCurrentUser: AuthService.reloadCurrentUser,
    getUid: AuthService.getUid,
    // actions
    signInWithGoogle,
    signInWithEmailPassword,
    registerWithEmailPassword,
    logInToSheets,
    logInAnonymously,
    logOut,
    postSignInSetup,
  };
}

export default useAuth;
